import type React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useInjection } from 'inversify-react';
import type { AppController } from '@/engine/app/App.controller';
import { TYPES } from '@/engine/ioc';
import { isWebGLSupported } from 'webgl-detector';
import type { IAboutInfo } from '@/engine/app/modules/ui/UIConfigSchema';
import { useSurgerySpecificUiConfigValue } from '@/engine/app/modules/ui';

export const WebglNotificationModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [crashOpen, setCrashOpen] = useState(false);

  const appController = useInjection<AppController>(TYPES.AppController);
  const aboutInfo = useSurgerySpecificUiConfigValue<IAboutInfo>('aboutInfo');
  const caseDetailsUrl = appController.getCaseState().caseDetailsUrl;

  const handleClose = (): void => {
    setOpen(false);
    window.location.assign(caseDetailsUrl);
  };

  const handleRefresh = (): void => {
    setCrashOpen(false);
    window.location.reload();
  };

  useEffect(() => {
    setOpen(!isWebGLSupported());
    document.addEventListener('webglcontextlost', () => {
      setCrashOpen(true);
    });
  }, []);

  return (
    <div>
      <Dialog open={open}>
        <ModalContainer>
          <DialogTitle id="screen-notification-dialog-title" sx={{ m: 0, p: 2, fontWeight: '600' }}>
            Note
          </DialogTitle>
          <DialogContent dividers>
            <ModalContentBody>
              Your current system, browser or settings do not support WebGL which is required for 3D visualizations.
              Please consult the{' '}
              <a href={aboutInfo.manualUrl} target="_blank" rel="noreferrer">
                Manual
              </a>{' '}
              for minimal system requirements and further advice
            </ModalContentBody>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'end', alignItems: 'center' }}>
            <Button onClick={handleClose}>Continue</Button>
          </DialogActions>
        </ModalContainer>
      </Dialog>
      <Dialog open={crashOpen}>
        <ModalContainer>
          <DialogTitle id="screen-notification-dialog-title" sx={{ m: 0, p: 2, fontWeight: '600' }}>
            Note
          </DialogTitle>
          <DialogContent dividers>
            <ModalContentBody>
              The WebGL feature of your browser, required for 3D visualizations, has crashed. Please try to refresh the
              plan. If the problem is not solved after refreshing, please consult the{' '}
              <a href={aboutInfo.manualUrl} target="_blank" rel="noreferrer">
                Manual
              </a>{' '}
              for troubleshooting advice.
            </ModalContentBody>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'end', alignItems: 'center' }}>
            <Button onClick={handleRefresh}>Refresh</Button>
          </DialogActions>
        </ModalContainer>
      </Dialog>
    </div>
  );
};

const ModalContainer = styled.div`
  width: 590px;
  padding-bottom: 10px;
`;

const ModalContentBody = styled.div`
  p.current-year {
    text-align: right;
    font-size: 0.8em;
  }
`;
